import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const PageClicksChart = ({ data }) => {
  // Format page names for display
  const formatPageName = (page) => {
    const pageNames = {
      'about': 'About',
      'music': 'Music',
      'events': 'Events',
      'shop': 'Shop',
      'gallery': 'Gallery',
      'contact': 'Contact',
      'news': 'News',
      'videos': 'Videos'
    };
    return pageNames?.[page] || page?.charAt(0)?.toUpperCase() + page?.slice(1);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      return (
        <div className="bg-white p-3 rounded-lg border border-border shadow-lg">
          <p className="font-medium text-text-primary">
            {formatPageName(label)}
          </p>
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-blue-600">{data?.value}</span> clicks
          </p>
        </div>
      );
    }
    return null;
  };

  const totalClicks = data?.reduce((sum, item) => sum + item?.clicks, 0) || 0;

  if (!data || data?.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Most Clicked Pages</h3>
          <p className="text-sm text-text-secondary">User interaction by app section</p>
        </div>
        
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No page click data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-text-primary">Most Clicked Pages</h3>
          <div className="text-sm text-text-secondary">
            Total: {totalClicks} clicks
          </div>
        </div>
        <p className="text-sm text-text-secondary">User interaction by app section</p>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
            <XAxis 
              dataKey="page"
              tickFormatter={formatPageName}
              stroke="#6b7280"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="clicks" 
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Top Pages Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <h4 className="font-medium text-text-primary mb-3">Top Performing Pages</h4>
        <div className="grid grid-cols-1 gap-2">
          {data?.slice(0, 3)?.map((page, index) => {
            const percentage = totalClicks > 0 ? ((page?.clicks / totalClicks) * 100) : 0;
            return (
              <div key={page?.page} className="flex items-center justify-between p-2 rounded-lg bg-background/50">
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    index === 0 ? 'bg-yellow-100 text-yellow-800' :
                    index === 1 ? 'bg-gray-100 text-gray-800': 'bg-orange-100 text-orange-800'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="font-medium text-text-primary">
                    {formatPageName(page?.page)}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-2 bg-border rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all duration-500"
                      style={{ width: `${Math.max(10, percentage)}%` }}
                    />
                  </div>
                  <span className="text-sm text-text-secondary min-w-[60px] text-right">
                    {page?.clicks} ({percentage?.toFixed(1)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Page Performance Insights */}
      {data?.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Insights</p>
              <p>
                The <span className="font-medium">{formatPageName(data?.[0]?.page)}</span> page is your most popular 
                with {data?.[0]?.clicks} clicks ({((data?.[0]?.clicks / totalClicks) * 100)?.toFixed(1)}% of total traffic).
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageClicksChart;